import { useQuery } from "@apollo/client";
import { withErrorBoundary } from "@sentry/react";
import moment from "moment";
import { useLocation } from "react-router-dom";
import {
  GetAskAiMeetingEmbedQueryQuery,
  GetAskAiMeetingEmbedQueryQueryVariables,
} from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import Avatars from "@components/avatar/avatars";
import ErrorFallback from "@components/error-fallback/error-fallback";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import {
  assertEdgesNonNullWithStringId,
  formatTime,
  getUrlUsingTypename,
  removeCurrentYear,
} from "@helpers/helpers";
import { pluralize } from "@helpers/string";

import getAskAiMeetingEmbedQuery from "../graphql/get-ask-ai-meeting-embed-query";
import AskAILink from "./ask-ai-link";

const AskAIMeetingCardComponent = ({ id }: { id: number | null }) => {
  const location = useLocation<TFLocationState>();
  const { data } = useQuery<
    GetAskAiMeetingEmbedQueryQuery,
    GetAskAiMeetingEmbedQueryQueryVariables
  >(getAskAiMeetingEmbedQuery, {
    variables: { meetingId: id || 0 },
    skip: !id,
    fetchPolicy: "cache-first",
  });
  const meeting = data?.meeting;

  if (!meeting) {
    return (
      <div className="flex gap-3 items-center">
        <TopicflowIcon type="meeting" className="w-4 h-4 text-gray-500" />
        <div className="text-gray-500 bg-gray-100 animate-pulse rounded-md h-6 flex-1" />
        <div className="text-gray-500 bg-gray-100 animate-pulse rounded-md h-6 w-24" />
      </div>
    );
  }

  const participantUsers = assertEdgesNonNullWithStringId(
    meeting.participants,
  ).map((participant) => participant.user);
  const topicCount = meeting.topics?.totalCount || 0;

  return (
    <div className="">
      <div className="flex gap-2 items-center">
        <TopicflowIcon type="meeting" className="w-4 h-4 text-gray-400" />
        <div className="flex-1 font-medium">
          <AskAILink
            to={getUrlUsingTypename(meeting, location)}
            className="hover:underline"
          >
            {meeting.title}
          </AskAILink>
        </div>
        <Avatars users={participantUsers} max={5} modalTitle="Participants" />
      </div>
      <div className="ml-6 mt-1 flex items-center gap-1">
        {meeting.startDatetime && meeting.endDatetime ? (
          <span>
            {removeCurrentYear(moment(meeting.startDatetime).format("ll"))},{" "}
            {formatTime(moment(meeting.startDatetime).format("h:mma"))}-
            {formatTime(moment(meeting.endDatetime).format("h:mma"))}
          </span>
        ) : (
          <span>Unscheduled</span>
        )}
        <span className="text-gray-500">&bull;</span>
        <span className="text-gray-500">
          {topicCount} {pluralize("topic", topicCount)}
        </span>
      </div>
    </div>
  );
};

const AskAIMeetingCard = withErrorBoundary(AskAIMeetingCardComponent, {
  fallback: ErrorFallback,
});

export default AskAIMeetingCard;
